import { fetchData, apiURL } from './api_helper';

// AI lesson generator — wraps the nexus-api /api/ai-lessons/* endpoints (spec §5.2).
// Mirrors the assignments.js convention: each call resolves result.data.
const base = `${apiURL}/api/ai-lessons`;

const getLimits = () => new Promise((resolve, reject) => {
  fetchData(`${base}/limits`, 'GET')
    .then(result => resolve(result.data)).catch(reject);
});

// Topic / goal / conversation-partner vocabularies for the speaking wizard, served from
// the live CORE catalogue (cached server-side, with a snapshot fallback). `targetLanguage`
// narrows the character roster to the personas CORE actually has for that language —
// Hungarian has two, so the old fixed list of eleven offered nine that CORE would reject.
// Topic and goal names come back localized to the request's Accept-Language.
// Resolves { topics, goals, characters, defaultCharacter, sources, ... }.
const getCatalogue = targetLanguage => new Promise((resolve, reject) => {
  // Query goes in the URL: fetchData turns a GET body into axiosConfig.data.
  fetchData(`${base}/catalogue?targetLanguage=${encodeURIComponent(targetLanguage || '')}`, 'GET')
    .then(result => resolve(result.data)).catch(reject);
});

const extract = body => new Promise((resolve, reject) => {
  fetchData(`${base}/extract`, 'POST', body)
    .then(result => resolve(result.data)).catch(reject);
});

const generate = body => new Promise((resolve, reject) => {
  fetchData(`${base}/generate`, 'POST', body)
    .then(result => resolve(result.data)).catch(reject);
});

// Crawl a web article URL server-side; resolves { text, title, sourceLanguage, truncated, meta }.
// `body` is { url, lessonType?, targetLanguage?, level?, sourceLanguage? }: when the extract
// params are present the backend also runs extract and returns `meta` (one round-trip).
const crawl = body => new Promise((resolve, reject) => {
  fetchData(`${base}/crawl`, 'POST', body)
    .then(result => resolve(result.data)).catch(reject);
});

// Upload a source document (PDF / DOC / DOCX / TXT / image) — the third source method. The
// backend extracts its text and runs the same analysis as a crawl, so this resolves the
// crawl's shape: { text, title, fileName, sourceLanguage, truncated, meta }. `params`
// are the crawl's optional extract params, sent as multipart fields.
const uploadDocument = (file, params) => new Promise((resolve, reject) => {
  fetchData(`${base}/upload`, 'FILE', { file, fields: params || {} })
    .then(result => resolve(result.data)).catch(reject);
});

// Preprocess a manually pasted source (clean noise, fix formatting, expand a thin
// source); resolves { text, action }. Used before extract on the manual-paste path.
const cleanSourceText = body => new Promise((resolve, reject) => {
  fetchData(`${base}/clean`, 'POST', body)
    .then(result => resolve(result.data)).catch(reject);
});

const getJob = jobId => new Promise((resolve, reject) => {
  fetchData(`${base}/jobs/${jobId}`, 'GET')
    .then(result => resolve(result.data)).catch(reject);
});

const cancelJob = jobId => new Promise((resolve, reject) => {
  fetchData(`${base}/jobs/${jobId}/cancel`, 'POST', {})
    .then(result => resolve(result.data)).catch(reject);
});

const get = id => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}`, 'GET')
    .then(result => resolve(result.data)).catch(reject);
});

// PUBLIC (no login): the listening PDF's QR player page. `key` is the random listen key
// printed in the QR, never the lesson id. Resolves { audioUrl, title, targetLanguage }.
const getListenAudio = key => new Promise((resolve, reject) => {
  fetchData(`${base}/listen/${encodeURIComponent(key)}`, 'GET', null, false)
    .then(result => resolve(result.data)).catch(reject);
});

// Bulk-sync the wizard's working snapshot before save/publish/pdf.
const updateSnapshot = (id, content, title) => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}/content`, 'PATCH', { content, title })
    .then(result => resolve(result.data)).catch(reject);
});

// AI ops are stateless: resolve { task } / { replies } the wizard merges locally.
// `tasks` is the current working snapshot (sibling context + count guard).
// `readingText` is the passage as currently edited above the tasks, so a regenerate
// reflects the teacher's edits instead of the original source.
const regenerateTask = (id, taskId, taskType, tasks, readingText) => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}/tasks/${taskId}/regenerate`, 'POST', { taskType, tasks, readingText })
    .then(result => resolve(result.data)).catch(reject);
});

// Review step, after the teacher edited the passage: which tasks the edit made stale.
// Resolves { checked, staleTaskIds } — checked:false = the check itself failed.
const passageCheck = (id, body) => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}/passage-check`, 'POST', body)
    .then(result => resolve(result.data)).catch(reject);
});

// Rebuild the stale tasks (mode 'stale', taskIds) or all of them (mode 'all') from the
// edited passage. Async like generate: resolves { jobId }, polled via getJob.
const regenerateFromPassage = (id, body) => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}/regenerate-from-passage`, 'POST', body)
    .then(result => resolve(result.data)).catch(reject);
});

const editTask = (id, taskId, task) => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}/tasks/${taskId}`, 'PATCH', { task })
    .then(result => resolve(result.data)).catch(reject);
});

const deleteTask = (id, taskId) => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}/tasks/${taskId}`, 'DELETE')
    .then(result => resolve(result.data)).catch(reject);
});

const editConversation = (id, conversation) => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}/conversation`, 'PATCH', { conversation })
    .then(result => resolve(result.data)).catch(reject);
});

const updateMeta = (id, meta) => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}`, 'PATCH', meta)
    .then(result => resolve(result.data)).catch(reject);
});

const save = id => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}/save`, 'POST', {})
    .then(result => resolve(result.data)).catch(reject);
});

// Speaking only. Materialises the lesson in CORE while the teacher is still on the
// Review step, so the preview can open the real thing. Unlike publish it does NOT lock
// the content, and creates no proxy lesson / favourite / assignment.
// Resolves { coreLessonId, corePushMocked }.
const previewSync = id => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}/preview-sync`, 'POST', {})
    .then(result => resolve(result.data)).catch(reject);
});

// Throw away an unpublished draft: retracts the CORE lesson (if the preview created
// one) and soft-deletes the log. 409 on a published lesson.
const discard = id => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}`, 'DELETE')
    .then(result => resolve(result.data)).catch(reject);
});

const publish = (id, body) => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}/publish`, 'POST', body)
    .then(result => resolve(result.data)).catch(reject);
});

// ---- listening ("Szöveg és hang" step) ----
// Script / voice / tempo edits. `patch` is { transcript?, speakers?: [{ name, persona }],
// speed?: 'slow'|'medium'|'fast'|'vfast'|null, title? }. Resolves the exposed lesson
// (its `listening` block carries the resolved cast and whether the audio is current).
const updateListening = (id, patch) => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}/listening`, 'PATCH', patch)
    .then(result => resolve(result.data)).catch(reject);
});

// Build the recording from the current script. Resolves { jobId, audio }: `audio` set
// (and jobId null) when the existing recording already matches; else poll getJob.
const buildAudio = id => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}/audio`, 'POST', {})
    .then(result => resolve(result.data)).catch(reject);
});

// One voice reading a fixed sentence. Resolves { mime, audioBase64 }.
const voiceSample = ({
  lang, persona, speed, level
}) => new Promise((resolve, reject) => {
  const q = [
    `lang=${encodeURIComponent(lang || '')}`,
    `persona=${encodeURIComponent(persona || '')}`,
    speed ? `speed=${encodeURIComponent(speed)}` : '',
    level ? `level=${encodeURIComponent(level)}` : ''
  ].filter(Boolean).join('&');
  fetchData(`${base}/voice-sample?${q}`, 'GET')
    .then(result => resolve(result.data)).catch(reject);
});

// Returns the raw arraybuffer response (api_helper switches to octet-stream when
// the body carries generatePdf). The caller turns it into a Blob download.
const exportPdf = (id, variant) => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}/pdf`, 'POST', { generatePdf: true, variant })
    .then(result => resolve(result)).catch(reject);
});

export default {
  getLimits,
  getCatalogue,
  extract,
  generate,
  crawl,
  uploadDocument,
  cleanSourceText,
  getJob,
  cancelJob,
  get,
  getListenAudio,
  updateSnapshot,
  regenerateTask,
  passageCheck,
  regenerateFromPassage,
  editTask,
  deleteTask,
  editConversation,
  updateMeta,
  save,
  previewSync,
  discard,
  publish,
  exportPdf,
  updateListening,
  buildAudio,
  voiceSample
};



// WEBPACK FOOTER //
// ./src/api/aiLessons.js